import type { IChatSessionWithState } from './types'
import { SESSION_DATE_FIELDS } from './types'
import { logger } from '@/utils/logger'

const STORAGE_PREFIX = 'sidd_chatbot_sessions'
const STORAGE_VERSION = '1.0'
const MAX_MESSAGES_PER_SESSION = 100
const CONTEXT = { component: 'ChatSessionStorage' }

export interface IPersistedSessionData {
  sessions: Record<string, IChatSessionWithState>
  activeSessionId: string | null
  version: string
  lastSaved: string
}

export function getStorageKey(chatbotId: string): string {
  return `${STORAGE_PREFIX}_${chatbotId}`
}

export function saveToLocalStorage(
  chatbotId: string,
  sessions: Record<string, IChatSessionWithState>,
  activeSessionId: string | null
): boolean {
  try {
    const key = getStorageKey(chatbotId)
    const prunedSessions = pruneSessionMessages(sessions)

    const data: IPersistedSessionData = {
      sessions: prunedSessions,
      activeSessionId,
      version: STORAGE_VERSION,
      lastSaved: new Date().toISOString(),
    }

    localStorage.setItem(key, JSON.stringify(data))
    return true
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'QuotaExceededError') {
        logger.warn(
          'localStorage quota exceeded, clearing old data',
          null,
          CONTEXT
        )
        try {
          clearLocalStorage(chatbotId)
          const minimalData: IPersistedSessionData = {
            sessions: {},
            activeSessionId: null,
            version: STORAGE_VERSION,
            lastSaved: new Date().toISOString(),
          }
          localStorage.setItem(
            getStorageKey(chatbotId),
            JSON.stringify(minimalData)
          )
        } catch (retryError) {
          logger.error(
            'Failed to save after clearing',
            null,
            CONTEXT,
            retryError as Error
          )
        }
      } else {
        logger.error('Failed to save to localStorage', null, CONTEXT, error)
      }
    }
    return false
  }
}

export function loadFromLocalStorage(
  chatbotId: string
): IPersistedSessionData | null {
  try {
    const key = getStorageKey(chatbotId)
    const stored = localStorage.getItem(key)

    if (!stored) {
      return null
    }

    const data = JSON.parse(stored, (key, value) => {
      type SessionDateField = (typeof SESSION_DATE_FIELDS)[number]
      if (
        typeof value === 'string' &&
        SESSION_DATE_FIELDS.includes(key as SessionDateField)
      ) {
        return new Date(value)
      }
      return value
    }) as IPersistedSessionData

    if (!data || typeof data !== 'object') {
      logger.warn('Invalid stored data structure', null, CONTEXT)
      return null
    }

    if (!data.sessions || typeof data.sessions !== 'object') {
      logger.warn('Invalid sessions data', null, CONTEXT)
      return null
    }

    return {
      sessions: data.sessions,
      activeSessionId: data.activeSessionId || null,
      version: data.version || '1.0',
      lastSaved: data.lastSaved || new Date().toISOString(),
    }
  } catch (error) {
    logger.error(
      'Failed to load from localStorage',
      null,
      CONTEXT,
      error as Error
    )
    clearLocalStorage(chatbotId)
    return null
  }
}

export function clearLocalStorage(chatbotId: string): void {
  try {
    const key = getStorageKey(chatbotId)
    localStorage.removeItem(key)
  } catch (error) {
    logger.error('Failed to clear localStorage', null, CONTEXT, error as Error)
  }
}

export function clearAllLocalStorage(): void {
  try {
    const keys = Object.keys(localStorage)
    const chatbotKeys = keys.filter(key => key.startsWith(STORAGE_PREFIX))

    chatbotKeys.forEach(key => {
      localStorage.removeItem(key)
    })
  } catch (error) {
    logger.error(
      'Failed to clear all localStorage',
      null,
      CONTEXT,
      error as Error
    )
  }
}

function pruneSessionMessages(
  sessions: Record<string, IChatSessionWithState>
): Record<string, IChatSessionWithState> {
  const pruned: Record<string, IChatSessionWithState> = {}

  Object.entries(sessions).forEach(([sessionId, session]) => {
    const prunedSession = { ...session }

    if (
      session.messages &&
      session.messages.length > MAX_MESSAGES_PER_SESSION
    ) {
      prunedSession.messages = session.messages.slice(-MAX_MESSAGES_PER_SESSION)
    }

    pruned[sessionId] = prunedSession
  })

  return pruned
}

export function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__sidd_chatbot_storage_test__'
    localStorage.setItem(testKey, 'test')
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}
